/**
 * MobileHeaderMenu - Sheet-based menu for mobile header widgets
 * Consolidates NotificationWidget, FavouritesWidget, TenantInfoDropdown, LanguageSwitcher
 * into a bottom Sheet triggered by "More" button (⋯)
 */

import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui';
import { MoreVertical } from 'lucide-react';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { NS } from '@/i18n/namespaces';
import { FavouritesWidget } from '../FavouritesWidget';
import { LanguageSwitcher } from '../LanguageSwitcher';
import { NotificationWidget } from '../NotificationWidget';
import { TenantInfoDropdown } from '../TenantInfoDropdown';
import { UserMenu } from '../UserMenu';
import { useHasAnyRole } from '../UserContextProvider/use-has-role';
import {
  useCloseMobileHeaderMenu,
  useMobileHeaderMenuIsOpen,
  useSetMobileHeaderMenuIsOpen,
  useToggleMobileHeaderMenu,
} from './MobileHeaderMenu.store';

export interface MobileHeaderMenuProps {
  /** Optional className for the trigger button */
  className?: string;
}

/**
 * MobileHeaderMenu - Trigger button + Sheet menu for mobile widgets
 */
const MobileHeaderMenuComponent: React.FC<MobileHeaderMenuProps> = ({ className }) => {
  const { t } = useTranslation(NS.COMMON);
  const isOpen = useMobileHeaderMenuIsOpen();
  const setIsOpen = useSetMobileHeaderMenuIsOpen();
  const toggle = useToggleMobileHeaderMenu();
  const _close = useCloseMobileHeaderMenu();
  const canSeeFavourites = useHasAnyRole('Root', 'NetOps', 'TechOps', 'NetworkAdmin');

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setIsOpen(open);
    },
    [setIsOpen]
  );

  return (
    <>
      {/* Trigger Button */}
      <button
        onClick={toggle}
        className={`p-2 hover:bg-accent rounded-md transition-colors ${className || ''}`}
        aria-label={t('MobileHeaderMenu_MoreButton', 'More options')}
      >
        <MoreVertical className="h-5 w-5" />
      </button>

      {/* Sheet Menu */}
      <Sheet open={isOpen} onOpenChange={handleOpenChange}>
        <SheetContent side="top" className="p-0 max-h-[80vh]">
          <SheetHeader className="px-4 py-3 border-b">
            <SheetTitle>{t('MobileHeaderMenu_Title', 'Menu')}</SheetTitle>
          </SheetHeader>

          <div className="p-4 space-y-4 overflow-y-auto">
            {/* User Menu Section */}
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-muted-foreground">
                {t('MobileHeaderMenu_Account', 'Account')}
              </h3>
              <UserMenu />
            </div>

            {/* Divider */}
            <div className="border-t" />

            {/* Notifications Section */}
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-muted-foreground">
                {t('MobileHeaderMenu_Notifications', 'Notifications')}
              </h3>
              <NotificationWidget />
            </div>

            {/* Favourites Section (role-based) */}
            {canSeeFavourites && (
              <div className="space-y-2">
                <h3 className="text-sm font-medium text-muted-foreground">
                  {t('MobileHeaderMenu_Favourites', 'Favourites')}
                </h3>
                <FavouritesWidget />
              </div>
            )}

            {/* Divider */}
            <div className="border-t" />

            {/* Network Info Section */}
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-muted-foreground">
                {t('MobileHeaderMenu_NetworkInfo', 'Network Info')}
              </h3>
              <TenantInfoDropdown />
            </div>

            {/* Language Switcher Section */}
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-muted-foreground">
                {t('MobileHeaderMenu_Language', 'Language')}
              </h3>
              <LanguageSwitcher className="w-full" />
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

MobileHeaderMenuComponent.displayName = 'MobileHeaderMenu';

export const MobileHeaderMenu = React.memo(MobileHeaderMenuComponent);
